/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Global Price WebSocket Manager
 *
 * Maintains singleton WebSocket connections for symbol price feeds.
 * Connections survive component unmounts (page navigation) and are only
 * closed when the user leaves the website (beforeunload) or when all
 * subscribers for a given symbol unsubscribe.
 *
 * Usage:
 *   const unsub = globalPriceManager.subscribe(symbol, accountId, timeframe, wsOrigin, callback);
 *   // callback receives { bid, ask } objects
 *   // call unsub() to unsubscribe (connection stays if other subscribers exist)
 */

const connections = new Map(); // key -> { ws, subscribers: Set, reconnectTimer, reconnectDelay }

function makeKey(symbol, accountId) {
  return `${symbol}::${accountId}`;
}

function connect(symbol, accountId, timeframe, wsOrigin) {
  const key = makeKey(symbol, accountId);
  const entry = connections.get(key);
  if (!entry) return;

  const authToken = localStorage.getItem('token');
  if (!authToken) return;

  try {
    const ws = new WebSocket(`${wsOrigin}/ws/prices/${accountId}/${symbol}/?token=${authToken}`);

    ws.onopen = () => {
      try {
        const tf = timeframe || 'M15';
        ws.send(JSON.stringify({ action: 'subscribe', timeframe: tf }));
        const e = connections.get(key);
        if (e) e.reconnectDelay = 2000; // reset backoff
      } catch (_) {}
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(String(event.data).replace(/NaN/g, 'null'));
        if (message?.type === 'live_price' || message?.data?.bid != null || message?.data?.ask != null) {
          const bid = message?.data?.bid ?? message?.bid ?? null;
          const ask = message?.data?.ask ?? message?.ask ?? null;
          if (bid == null && ask == null) return;
          const e = connections.get(key);
          if (e) {
            e.subscribers.forEach((cb) => {
              try { cb({ bid, ask }); } catch (_) {}
            });
          }
        }
      } catch (_) {}
    };

    ws.onerror = () => {};

    ws.onclose = () => {
      const e = connections.get(key);
      if (!e || e.subscribers.size === 0) return;
      // Only reconnect if WE are still the current socket for this entry.
      // If subscribe() already replaced us with a new socket, skip.
      if (e.ws !== ws) return;
      // Reconnect with backoff
      const delay = e.reconnectDelay || 2000;
      e.reconnectTimer = setTimeout(() => {
        connect(symbol, accountId, timeframe, wsOrigin);
      }, delay);
      e.reconnectDelay = Math.min(delay * 1.5, 30000);
    };

    const e = connections.get(key);
    if (e) {
      e.ws = ws;
    }
  } catch (err) {
    console.warn('[GlobalPriceManager] WS open failed', symbol, err);
  }
}

// Grace period (ms) before closing a connection with 0 subscribers.
// Allows fast re-subscribe if a component effect re-runs quickly.
const IDLE_CLOSE_DELAY = 10_000; // 10 seconds

function subscribe(symbol, accountId, timeframe, wsOrigin, callback) {
  const key = makeKey(symbol, accountId);
  let entry = connections.get(key);

  if (!entry) {
    entry = { ws: null, subscribers: new Set(), reconnectTimer: null, reconnectDelay: 2000, idleTimer: null };
    connections.set(key, entry);
    connect(symbol, accountId, timeframe, wsOrigin);
  } else {
    // Cancel any pending idle-close since we have a new subscriber
    if (entry.idleTimer) { clearTimeout(entry.idleTimer); entry.idleTimer = null; }
    // If the WS is dead (closed/closing) and wasn't reconnecting, reconnect now
    if (!entry.ws || entry.ws.readyState >= WebSocket.CLOSING) {
      connect(symbol, accountId, timeframe, wsOrigin);
    }
  }

  entry.subscribers.add(callback);

  // Return unsubscribe function
  return () => {
    const e = connections.get(key);
    if (!e) return;
    e.subscribers.delete(callback);
    // When no subscribers remain, schedule cleanup after a grace period
    if (e.subscribers.size === 0 && !e.idleTimer) {
      e.idleTimer = setTimeout(() => {
        const cur = connections.get(key);
        if (!cur || cur.subscribers.size > 0) return; // re-subscribed in the meantime
        if (cur.reconnectTimer) clearTimeout(cur.reconnectTimer);
        if (cur.ws) {
          try {
            if (cur.ws.readyState === WebSocket.OPEN) {
              cur.ws.send(JSON.stringify({ action: 'unsubscribe' }));
            }
          } catch (_) {}
          try { cur.ws.close(); } catch (_) {}
        }
        connections.delete(key);
      }, IDLE_CLOSE_DELAY);
    }
  };
}

function closeAll() {
  connections.forEach((entry, key) => {
    if (entry.reconnectTimer) clearTimeout(entry.reconnectTimer);
    if (entry.idleTimer) clearTimeout(entry.idleTimer);
    if (entry.ws) {
      try {
        if (entry.ws.readyState === WebSocket.OPEN) {
          entry.ws.send(JSON.stringify({ action: 'unsubscribe' }));
        }
      } catch (_) {}
      try { entry.ws.close(); } catch (_) {}
    }
  });
  connections.clear();
}

// Close all connections when user leaves the website
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', closeAll);
}

const globalPriceManager = { subscribe, closeAll };
export default globalPriceManager;
